export function buildReactD3Tree(data, userEmpCd) {
  if (!Array.isArray(data) || data.length === 0) return null;

  // helpers (non-mutating normalization)
  const norm = v => (typeof v === 'string' ? v.trim() : v);
  const toInt = v => (v == null || v === '' ? null : parseInt(v, 10));

  // Indexes
  const byCode = new Map(); // EmpCd -> node
  const nameToCodes = new Map(); // EmpName -> Set(EmpCd)
  const raw = data.map(e => ({
    EmpCd: norm(e.EmpCd),
    EmpName: norm(e.EmpName),
    Designation: e.Designation,
    Imagefile: e.Imagefile,
    DirectMgr: norm(e.DirectMgr), // prefer manager CODE if available
    DirectMgrName: norm(e.DirectMgrName), // fallback
    Lvl: toInt(e.Lvl),
  }));

  const employee = raw?.filter(
    emp => emp?.EmpCd?.toLowerCase().trim() === userEmpCd?.toLowerCase().trim(),
  )?.[0];

  // Create nodes (no mutation of raw)
  raw.forEach(e => {
    if (!e.EmpCd) {
      // skip invalid rows
      return;
    }
    if (byCode.has(e.EmpCd)) {
      // Duplicate EmpCd found; keep the first and skip duplicates to avoid overwriting
      return;
    }
    if (
      employee?.DirectMgrName === e?.EmpName &&
      e?.DirectMgrName === employee?.EmpName
    ) {
      e.DirectMgr = null;
      e.DirectMgrName = null;
    }

    const node = {
      name: e.EmpName || e.EmpCd,
      attributes: {
        Title: e.Designation ?? '',
        Code: e.EmpCd,
        Imagefile: e.Imagefile ?? null,
      },
      children: [],
      // keep a pointer for cycle detection if needed
      meta: { Lvl: e.Lvl, mgrCode: e.DirectMgr, mgrName: e.DirectMgrName },
    };
    byCode.set(e.EmpCd, node);

    if (e.EmpName) {
      if (!nameToCodes.has(e.EmpName)) nameToCodes.set(e.EmpName, new Set());
      nameToCodes.get(e.EmpName).add(e.EmpCd);
    }
  });

  // Track "possible roots": start with everyone; remove anyone who becomes a child
  const possibleRoots = new Set(byCode.keys());
  const orphans = [];
  const selfParents = [];

  // Link children
  Array.from(byCode.entries()).forEach(([code, node]) => {
    const { mgrCode, mgrName } = node.meta;

    // determine parent (prefer DirectMgr code)
    let parentNode = null;
    if (mgrCode && byCode.has(mgrCode)) {
      parentNode = byCode.get(mgrCode);
    } else if (mgrName && nameToCodes.has(mgrName)) {
      const codes = Array.from(nameToCodes.get(mgrName));
      if (codes.length === 1 && byCode.has(codes[0])) {
        parentNode = byCode.get(codes[0]);
      } else {
        // ambiguous or unknown manager by name
        orphans.push({
          child: code,
          reason: `Manager name "${mgrName}" ambiguous or missing`,
        });
      }
    } else if (mgrCode || mgrName) {
      orphans.push({ child: code, reason: 'Manager not found in dataset' });
    }

    if (parentNode) {
      if (parentNode.attributes.Code === code) {
        selfParents.push(code);
      } else {
        parentNode.children.push(node);
        possibleRoots.delete(code);
      }
    }
  });

  // Choose roots
  let roots = Array.from(possibleRoots).map(c => byCode.get(c));

  // If we somehow have no roots, try a fallback: pick lowest Lvl entries
  if (roots.length === 0) {
    let minLvl = Infinity;
    Array.from(byCode.values()).forEach(n => {
      const L = n.meta.Lvl ?? Infinity;
      if (L < minLvl) minLvl = L;
    });
    roots = Array.from(byCode.values()).filter(
      n => (n.meta.Lvl ?? Infinity) === minLvl,
    );
  }

  // Basic cycle detection (defensive)
  const seen = new Set();
  const stack = new Set();
  const cycles = [];

  const dfs = n => {
    const id = n.attributes.Code;
    if (stack.has(id)) {
      cycles.push(id);
      return;
    }
    if (seen.has(id)) return;
    seen.add(id);
    stack.add(id);
    n.children.forEach(c => dfs(c));
    stack.delete(id);
  };
  roots.forEach(dfs);

  // Clean meta to avoid leaking internals to react-d3-tree
  const stripMeta = n => {
    delete n.meta;
    n.children.forEach(stripMeta);
  };
  roots.forEach(stripMeta);

  // react-d3-tree accepts a single root object or an array (forest)
  if (roots.length === 0) {
    return null;
  }
  if (roots.length === 1) {
    return roots[0];
  }
  return roots;
}

export function buildMiniTree(data, userEmpCd) {
  if (data && data.length > 0) {
    const normalize = str => str?.trim().toLowerCase();
    const userCode = normalize(userEmpCd);

    const self = data.find(emp => normalize(emp.EmpCd) === userCode);

    if (!self) return [];

    const parent = data.find(
      emp => normalize(emp.EmpName) === normalize(self.DirectMgrName),
    );

    const allDirectReports = data.filter(
      emp =>
        normalize(emp.DirectMgrName) === normalize(self.EmpName) &&
        normalize(emp.EmpName) !== normalize(parent?.EmpName),
    );

    const directReports = allDirectReports.slice(0, 1);

    const extraCount = allDirectReports.length - 1;
    if (extraCount > 0) {
      directReports.push({
        name: `+${extraCount}`,
        isExtra: true, // flag to detect it later
      });
    }

    const selfNode = {
      name: self.EmpName.trim(),
      attributes: {
        Title: self.Designation,
        Code: self.EmpCd.trim(),
        Imagefile: self.Imagefile,
      },
      children: directReports.map(child => {
        if (child.isExtra) {
          return {
            name: child.name,
            isExtra: true,
          };
        }

        return {
          name: child.EmpName.trim(),
          attributes: {
            Title: child.Designation,
            Code: child.EmpCd.trim(),
            Imagefile: child.Imagefile,
          },
        };
      }),
    };

    if (parent) {
      return [
        {
          name: parent.EmpName.trim(),
          attributes: {
            Title: parent.Designation,
            Code: parent.EmpCd.trim(),
            Imagefile: parent.Imagefile,
          },
          children: [selfNode],
        },
      ];
    }

    return [selfNode];
  }

  return [];
}
